import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObjectOutlined';
import CodeIcon from '@mui/icons-material/CodeOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { adminCatalogApi, CatalogRow, CatalogSlug } from '../../../api/adminCatalogApi';
import { paths } from '../../../routes/paths';
import { colors, primaryButtonSx, tableCellSx, tableHeadCellSx, typo } from '../../../theme/tokens';
import { WriteButton, WriteIconButton } from '../../../components/ui';
import { useWriteGuard } from '../../../context/useWriteGuard';

import SchemaEditorDrawer from './SchemaEditorDrawer';

/**
 * Shell for JSON-schema-bearing catalogs (component_type,
 * onboarding_component). Mirrors the plain `CatalogShell` shape but
 * adds a `schema_json` column (key count badge) and a "Schema" action
 * column that opens a side drawer with Monaco (or textarea fallback).
 *
 * Kept separate from CatalogShell so plain catalogs (task_types, etc)
 * don't inherit the schema column/drawer overhead they don't need.
 */

export interface SchemaCatalogShellProps {
  slug: CatalogSlug;
  title: string;
  emptyBody: string;
  addCtaLabel: string;
  breadcrumbLabel: string;
  /** Hint shown under the header — what the schema is for. */
  schemaHint: string;
}

type EditingCell = {
  code: string;
  field: 'name' | 'description';
  value: string;
} | null;

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

function jsonKeyCount(obj: object | null | undefined): number | null {
  if (!obj) return null;
  try {
    return Object.keys(obj).length;
  } catch {
    return null;
  }
}

const SchemaCatalogShell: React.FC<SchemaCatalogShellProps> = ({
  slug,
  title,
  emptyBody,
  addCtaLabel,
  breadcrumbLabel,
  schemaHint,
}) => {
  const navigate = useNavigate();
  const { readOnly } = useWriteGuard();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['admin', 'catalogs', slug], [slug]);

  const listQuery = useQuery<CatalogRow[]>({
    queryKey,
    queryFn: () => adminCatalogApi.list(slug),
  });

  const [editing, setEditing] = useState<EditingCell>(null);
  const [newForm, setNewForm] = useState<{ code: string; name: string; description: string }>({
    code: '',
    name: '',
    description: '',
  });
  const [showAdd, setShowAdd] = useState(false);
  const [schemaFor, setSchemaFor] = useState<CatalogRow | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing?.code, editing?.field]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = listQuery.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async (args: {
      code: string;
      patch: { name?: string; description?: string | null; active?: boolean; schema_json?: object | null };
    }) => adminCatalogApi.update(slug, args.code, args.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setEditing(null);
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Update failed', sev: 'error' });
      setEditing(null);
    },
  });

  const schemaSaveMutation = useMutation({
    mutationFn: async (args: { code: string; schema_json: object | null }) =>
      adminCatalogApi.update(slug, args.code, { schema_json: args.schema_json }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSchemaFor(null);
      setSnackbar({ open: true, msg: 'Schema saved', sev: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Schema save failed', sev: 'error' });
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      return adminCatalogApi.create(slug, {
        code: newForm.code.trim(),
        name: newForm.name.trim(),
        description: newForm.description.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setNewForm({ code: '', name: '', description: '' });
      setShowAdd(false);
      setSnackbar({ open: true, msg: 'Row added', sev: 'success' });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'conflict') {
        setSnackbar({ open: true, msg: 'Code already exists', sev: 'error' });
      } else {
        setSnackbar({ open: true, msg: 'Create failed', sev: 'error' });
      }
    },
  });

  const commitEdit = useCallback(() => {
    if (!editing) return;
    const current = rows.find((r) => r.code === editing.code);
    if (!current) {
      setEditing(null);
      return;
    }
    const originalVal = editing.field === 'name' ? current.name : current.description ?? '';
    if (editing.value === originalVal) {
      setEditing(null);
      return;
    }
    updateMutation.mutate({
      code: editing.code,
      patch:
        editing.field === 'name'
          ? { name: editing.value.trim() }
          : { description: editing.value.trim() || null },
    });
  }, [editing, rows, updateMutation]);

  const startEdit = (code: string, field: 'name' | 'description', value: string) => {
    setEditing({ code, field, value });
  };

  const showEmpty = !listQuery.isLoading && rows.length === 0;

  return (
    <Box sx={{ maxWidth: 1200 }}>
      {/* Breadcrumb */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1,
        }}
      >
        <MuiLink
          onClick={() => navigate(paths.admin.home())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mr: 0.5 }}
        >
          Admin
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.catalogs.root())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Reference Catalogs
        </MuiLink>
        /
        <span style={{ marginLeft: 4, color: colors.textPrimary }}>{breadcrumbLabel}</span>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>{title}</Typography>
          <Typography sx={{ ...typo.pageSubtitle }}>
            {rows.length} {rows.length === 1 ? 'row' : 'rows'} · {schemaHint}
          </Typography>
        </Box>
        {!showAdd && !showEmpty && (
          <WriteButton
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            Add row
          </WriteButton>
        )}
      </Box>

      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.adminAccent }} />
        </Box>
      ) : showEmpty && !showAdd ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
            py: 8,
            px: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: colors.adminAccentSubtle,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <AddIcon sx={{ fontSize: 36, color: colors.adminAccent }} />
          </Box>
          <Typography sx={{ ...typo.sectionTitle, fontSize: 18, mb: 1 }}>
            {title} is empty
          </Typography>
          <Typography sx={{ ...typo.secondaryText, mx: 'auto', maxWidth: 460, mb: 3 }}>
            {emptyBody}
          </Typography>
          <WriteButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            {addCtaLabel}
          </WriteButton>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeadCellSx, width: 220 }}>Code</TableCell>
                  <TableCell sx={tableHeadCellSx}>Name</TableCell>
                  <TableCell sx={tableHeadCellSx}>Description</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 110 }}>Schema</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 96 }}>Active</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 140 }}>Updated</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 100, textAlign: 'center' }}>
                    Edit JSON
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isEditingName = editing?.code === r.code && editing.field === 'name';
                  const isEditingDesc =
                    editing?.code === r.code && editing.field === 'description';
                  const keyCount = jsonKeyCount(r.schema_json);
                  return (
                    <TableRow key={r.code} hover>
                      <TableCell sx={{ ...tableCellSx, ...typo.mono, color: colors.textPrimary }}>
                        {r.code}
                      </TableCell>
                      <TableCell
                        sx={{ ...tableCellSx, cursor: isEditingName ? 'text' : 'pointer' }}
                        onDoubleClick={() => !readOnly && startEdit(r.code, 'name', r.name)}
                        data-write-action="true"
                      >
                        {isEditingName ? (
                          <TextField
                            size="small"
                            value={editing.value}
                            inputRef={inputRef}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            inputProps={{ maxLength: 100 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13 }}>{r.name}</Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          cursor: isEditingDesc ? 'text' : 'pointer',
                          color: colors.textSecondary,
                        }}
                        onDoubleClick={() =>
                          !readOnly && startEdit(r.code, 'description', r.description ?? '')
                        }
                        data-write-action="true"
                      >
                        {isEditingDesc ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={editing.value}
                            inputRef={inputRef}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') setEditing(null);
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                            {r.description || (
                              <span style={{ color: colors.textTertiary, fontStyle: 'italic' }}>
                                Double-click to add…
                              </span>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        {keyCount != null ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontSize: 11,
                              color: colors.textSecondary,
                              backgroundColor: colors.bgMuted,
                              px: 0.75,
                              py: 0.25,
                              borderRadius: '4px',
                            }}
                          >
                            <DataObjectIcon sx={{ fontSize: 13 }} />
                            {`{${keyCount}}`}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Switch
                          size="small"
                          checked={r.active}
                          onChange={(e) =>
                            updateMutation.mutate({
                              code: r.code,
                              patch: { active: e.target.checked },
                            })
                          }
                          disabled={updateMutation.isPending || readOnly}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                        {formatRelative(r.updated_at ?? r.created_at)}
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                        <Tooltip title="Edit schema_json in Monaco">
                          <WriteIconButton
                            size="small"
                            onClick={() => setSchemaFor(r)}
                            sx={{ color: colors.adminAccent }}
                          >
                            <CodeIcon fontSize="small" />
                          </WriteIconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {showAdd && (
                  <TableRow sx={{ backgroundColor: colors.adminAccentSubtle }}>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        placeholder="new-code"
                        value={newForm.code}
                        onChange={(e) => setNewForm({ ...newForm, code: e.target.value })}
                        inputProps={{ maxLength: 50 }}
                        InputProps={{
                          sx: { fontFamily: 'Fira Code, monospace', fontSize: 12 },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        placeholder="Display name"
                        value={newForm.name}
                        onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                        inputProps={{ maxLength: 100 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Optional description"
                        value={newForm.description}
                        onChange={(e) =>
                          setNewForm({ ...newForm, description: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell sx={tableCellSx}>—</TableCell>
                    <TableCell sx={tableCellSx}>—</TableCell>
                    <TableCell sx={tableCellSx}>—</TableCell>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save (Enter)">
                          <span>
                            <WriteIconButton
                              size="small"
                              disabled={
                                !newForm.code.trim() ||
                                !newForm.name.trim() ||
                                createMutation.isPending
                              }
                              onClick={() => createMutation.mutate()}
                              sx={{ color: colors.successIcon }}
                            >
                              <CheckIcon fontSize="small" />
                            </WriteIconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel (Esc)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowAdd(false);
                              setNewForm({ code: '', name: '', description: '' });
                            }}
                            sx={{ color: colors.textSecondary }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!showAdd && rows.length > 0 && (
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setShowAdd(true)} data-write-action="true">
                    <TableCell colSpan={7} sx={{ ...tableCellSx, py: 1, textAlign: 'center' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          color: colors.adminAccent,
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        <AddIcon fontSize="small" /> Add row
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {listQuery.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load.
        </Alert>
      )}

      {schemaFor && (
        <SchemaEditorDrawer
          open={!!schemaFor}
          onClose={() => (schemaSaveMutation.isPending ? undefined : setSchemaFor(null))}
          code={schemaFor.code}
          name={schemaFor.name}
          value={schemaFor.schema_json}
          saving={schemaSaveMutation.isPending}
          onSave={(next) =>
            schemaSaveMutation.mutate({ code: schemaFor.code, schema_json: next })
          }
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchemaCatalogShell;
